<template>
  <div class="favorites-container">
    <DefaultHeader
      title="Избранное"
      back
      class="fixed-header"
      :offset="640"
    />

    <main class="favorites-scroll">
      <div class="favorites-page">

        <!-- ================================================= -->
        <!-- HERO -->
        <!-- ================================================= -->

        <section class="favorites-hero">
          <div class="hero-content">
            <div class="hero-label">
              <span class="hero-label-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 20.2C12 20.2 4.5 15.8 4.5 9.6C4.5 6.6 6.7 4.7 9.2 4.7C10.6 4.7 11.6 5.4 12 6.1C12.4 5.4 13.4 4.7 14.8 4.7C17.3 4.7 19.5 6.6 19.5 9.6C19.5 15.8 12 20.2 12 20.2Z"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>

              <span>
                Сохранённые вакансии
              </span>
            </div>

            <h1 class="hero-title">
              Ваше избранное
            </h1>

            <p class="hero-subtitle">
              Все интересные вакансии в одном месте.
              Возвращайтесь к ним, когда будете готовы откликнуться.
            </p>
          </div>

          <div
            v-if="!isLoading && favorites.length"
            class="favorites-counter"
          >
            <span class="counter-number">
              {{ favorites.length }}
            </span>

            <span class="counter-label">
              {{ favoritesCountLabel }}
            </span>
          </div>

          <div class="hero-decoration">
            <div class="hero-glow" />

            <svg
              class="hero-heart"
              viewBox="0 0 120 120"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M60 98C60 98 22 76 22 45C22 30 33 20 46 20C53 20 58 24 60 28C62 24 67 20 74 20C87 20 98 30 98 45C98 76 60 98 60 98Z"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </div>
        </section>

        <!-- ================================================= -->
        <!-- LOADING -->
        <!-- ================================================= -->

        <div
          v-if="isLoading"
          class="favorites-grid favorites-grid-loading"
        >
          <div
            v-for="i in 6"
            :key="i"
            class="favorite-card-slot"
          >
            <FavoriteSkeleton
              class="favorite-skeleton-card"
            />
          </div>
        </div>

        <!-- ================================================= -->
        <!-- ERROR -->
        <!-- ================================================= -->

        <div
          v-else-if="loadError"
          class="state-wrapper"
        >
          <div class="state-card">
            <div class="state-icon state-icon-error">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 7.5V12.5"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />

                <circle
                  cx="12"
                  cy="16"
                  r="1"
                  fill="currentColor"
                />

                <path
                  d="M10.2 4.3L3.3 16.5C2.6 17.8 3.5 19.5 5 19.5H19C20.5 19.5 21.4 17.8 20.7 16.5L13.8 4.3C13 2.9 11 2.9 10.2 4.3Z"
                  stroke="currentColor"
                  stroke-width="1.55"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <h3 class="state-title">
              Не удалось загрузить избранное
            </h3>

            <p class="state-description">
              Что-то пошло не так. Попробуйте загрузить список ещё раз.
            </p>

            <button
              class="retry-button"
              type="button"
              @click="loadFavorites"
            >
              Повторить
            </button>
          </div>
        </div>

        <!-- ================================================= -->
        <!-- EMPTY -->
        <!-- ================================================= -->

        <div
          v-else-if="favorites.length === 0"
          class="state-wrapper"
        >
          <div class="state-card">
            <div class="empty-image-wrapper">
              <img
                src="../assets/empty-heart.png"
                alt=""
                class="empty-image"
              />
            </div>

            <h3 class="state-title">
              Здесь пока пусто
            </h3>

            <p class="state-description">
              Сохраняйте понравившиеся вакансии,
              чтобы быстро вернуться к ним позже.
            </p>

            <div class="empty-hint">
              <span class="empty-hint-heart">
                ♥
              </span>

              <span>
                Нажмите на сердечко в карточке вакансии
              </span>
            </div>
          </div>
        </div>

        <!-- ================================================= -->
        <!-- FAVORITES -->
        <!-- ================================================= -->

        <div
          v-else
          class="favorites-grid"
        >
          <div
            v-for="vac in favorites"
            :key="vac.vacancy.vacancy_id"
            class="favorite-card-slot"
          >
            <FavoriteVacancy
              class="favorite-vacancy-card"
              :vacancy="{
                vacancy_id: vac.vacancy.vacancy_id,
                title: vac.vacancy.title,
                company_name: vac.company.name,
                info: vac.vacancy.info,
                company_logo_url: vac.company.urlOfLogo
              }"
              @remove="handleRemove"
              @open="openVacancy"
            />
          </div>
        </div>

      </div>
    </main>

    <Footer class="fixed-footer" />

    <!-- ===================================================== -->
    <!-- TOAST -->
    <!-- ===================================================== -->

    <transition name="toast-fade">
      <div
        v-if="toastVisible"
        class="toast"
        role="status"
        aria-live="polite"
      >
        <span class="toast-icon">
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 10.2L8.2 13.2L15 6.7"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";

import { useRouter } from "vue-router";

import DefaultHeader from "../components/FavoritesHeader.vue";
import FavoriteVacancy from "../components/FavoriteVacancy.vue";
import FavoriteSkeleton from "../components/FavoriteSkeleton.vue";
import Footer from "../components/MiniFooter.vue";

import FavoritesService from "../api/FavoritesService";

const router = useRouter();

/* ========================================================= */
/* STATE */
/* ========================================================= */

const favorites = ref([]);

const isLoading = ref(true);
const loadError = ref(false);

const toastMessage = ref("");
const toastVisible = ref(false);

let toastTimer = null;

/* ========================================================= */
/* COUNT */
/* ========================================================= */

const favoritesCountLabel = computed(() => {
  const count = favorites.value.length;

  const lastTwo =
    count % 100;

  const last =
    count % 10;

  if (
    lastTwo >= 11 &&
    lastTwo <= 19
  ) {
    return "вакансий";
  }

  if (last === 1) {
    return "вакансия";
  }

  if (
    last >= 2 &&
    last <= 4
  ) {
    return "вакансии";
  }

  return "вакансий";
});

/* ========================================================= */
/* TOAST */
/* ========================================================= */

function showToast(message) {
  toastMessage.value = message;
  toastVisible.value = true;

  if (toastTimer) {
    clearTimeout(toastTimer);
  }

  toastTimer =
    window.setTimeout(
      () => {
        toastVisible.value = false;
      },
      1800
    );
}

/* ========================================================= */
/* LOAD */
/* ========================================================= */

async function loadFavorites() {
  isLoading.value = true;
  loadError.value = false;

  try {
    const response =
      await FavoritesService
        .getFavorites();

    favorites.value =
      response?.vacancies ?? [];
  } catch (error) {
    console.error(
      "❌ Ошибка при загрузке избранного:",
      error
    );

    favorites.value = [];
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

/* ========================================================= */
/* REMOVE */
/* ========================================================= */

async function handleRemove(vacancyId) {
  const original =
    [...favorites.value];

  favorites.value =
    favorites.value.filter(
      item =>
        item.vacancy.vacancy_id !==
        vacancyId
    );

  try {
    await FavoritesService
      .toggleFavorite(vacancyId);

    showToast(
      "Удалено из избранного"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка удаления:",
      error
    );

    favorites.value =
      original;

    showToast(
      "Не удалось удалить вакансию"
    );
  }
}

/* ========================================================= */
/* OPEN */
/* ========================================================= */

function openVacancy(vacancyId) {
  if (!vacancyId) {
    return;
  }

  router.push({
    name: "Vacancy",

    params: {
      vacancyId
    }
  });
}

/* ========================================================= */
/* LIFECYCLE */
/* ========================================================= */

onMounted(() => {
  loadFavorites();
});

onBeforeUnmount(() => {
  if (toastTimer) {
    clearTimeout(toastTimer);
  }
});
</script>

<style scoped>
/* ========================================================= */
/* PAGE */
/* ========================================================= */

.favorites-container {
  --header-height: 60px;
  --footer-height: 60px;

  position: relative;

  width: 100%;
  height: 100vh;
  height: 100dvh;

  overflow: hidden;

  background:
    linear-gradient(
      180deg,
      #f7f8fa 0%,
      #f4f6f8 100%
    );

  color:
    #252a31;
}

/* ========================================================= */
/* HEADER / FOOTER */
/* ========================================================= */

.fixed-header {
  position: fixed;

  z-index: 1000;

  top: 0;
  left: 0;
  right: 0;
}

.fixed-footer {
  position: fixed;

  z-index: 900;

  left: 0;
  right: 0;
  bottom: 0;

  width: 100%;

  background:
    #ffffff;

  border-top:
    1px solid
    rgba(30, 40, 55, 0.055);
}

/* ========================================================= */
/* SCROLL */
/* ========================================================= */

.favorites-scroll {
  position: absolute;

  top:
    var(--header-height);

  right: 0;

  bottom:
    var(--footer-height);

  left: 0;

  overflow-x: hidden;
  overflow-y: auto;

  overscroll-behavior:
    contain;

  -webkit-overflow-scrolling:
    touch;

  scrollbar-width:
    thin;

  scrollbar-color:
    rgba(90, 100, 115, 0.15)
    transparent;
}

.favorites-scroll::-webkit-scrollbar {
  width: 6px;
}

.favorites-scroll::-webkit-scrollbar-track {
  background:
    transparent;
}

.favorites-scroll::-webkit-scrollbar-thumb {
  border-radius:
    999px;

  background:
    rgba(90, 100, 115, 0.15);
}

/* ========================================================= */
/* CONTENT */
/* ========================================================= */

.favorites-page {
  width: 100%;
  max-width: 1160px;

  min-height: 100%;

  margin:
    0 auto;

  padding:
    32px
    32px
    72px;

  box-sizing:
    border-box;
}

/* ========================================================= */
/* HERO */
/* ========================================================= */

.favorites-hero {
  position: relative;

  min-height: 154px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 28px;

  padding:
    28px 30px;

  overflow: hidden;

  border:
    1px solid
    rgba(30, 45, 65, 0.07);

  border-radius:
    24px;

  background:
    linear-gradient(
      145deg,
      #ffffff 0%,
      #fbfcfd 100%
    );

  box-shadow:
    0 10px 30px
    rgba(20, 30, 45, 0.055);
}

.hero-content {
  position: relative;
  z-index: 2;

  max-width: 610px;
}

.hero-label {
  display: inline-flex;
  align-items: center;

  gap: 7px;

  margin-bottom:
    12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1;

  letter-spacing:
    0.35px;

  text-transform:
    uppercase;

  color:
    #747c86;
}

.hero-label-icon {
  width: 22px;
  height: 22px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius:
    7px;

  background:
    rgba(40, 135, 247, 0.08);

  color:
    #2887f7;
}

.hero-label-icon svg {
  width: 13px;
  height: 13px;
}

.hero-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;

  letter-spacing:
    -0.45px;

  color:
    #20242a;
}

.hero-subtitle {
  max-width: 530px;

  margin:
    10px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11.5px;
  line-height: 1.5;

  color:
    #858b93;
}

/* ========================================================= */
/* COUNTER */
/* ========================================================= */

.favorites-counter {
  position: relative;
  z-index: 3;

  min-width: 92px;

  display: flex;
  flex-direction: column;
  align-items: flex-end;

  padding:
    12px 14px;

  border:
    1px solid
    rgba(40, 135, 247, 0.09);

  border-radius:
    14px;

  background:
    rgba(40, 135, 247, 0.055);
}

.counter-number {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;
  line-height: 1;

  color:
    #2887f7;
}

.counter-label {
  margin-top:
    5px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8.5px;

  color:
    #7e8792;
}

/* ========================================================= */
/* HERO DECORATION */
/* ========================================================= */

.hero-decoration {
  position: absolute;

  z-index: 1;

  top: 0;
  right: 0;

  width: 260px;
  height: 100%;

  pointer-events:
    none;
}

.hero-glow {
  position: absolute;

  width: 260px;
  height: 260px;

  top: -120px;
  right: -70px;

  border-radius:
    50%;

  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.12) 0%,
      rgba(40, 135, 247, 0.03) 48%,
      transparent 72%
    );
}

.hero-heart {
  position: absolute;

  width: 122px;
  height: 122px;

  right: 50px;
  bottom: -40px;

  color:
    rgba(40, 135, 247, 0.06);

  transform:
    rotate(-8deg);
}

/* ========================================================= */
/* GRID */
/* ========================================================= */

.favorites-grid {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  column-gap: 28px;
  row-gap: 26px;

  margin-top:
    28px;

  align-items:
    start;
}

.favorite-card-slot {
  width: 100%;
  min-width: 0;

  display: flex;
}

.favorite-vacancy-card,
.favorite-skeleton-card {
  width: 100% !important;
  max-width: 100% !important;

  min-width: 0;

  box-sizing:
    border-box;
}

.favorites-grid-loading {
  opacity: 0.98;
}

/* ========================================================= */
/* STATE */
/* ========================================================= */

.state-wrapper {
  min-height: 450px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding:
    50px 0;
}

.state-card {
  width: 100%;
  max-width: 460px;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding:
    38px 32px;

  box-sizing:
    border-box;

  text-align: center;

  border:
    1px solid
    rgba(30, 45, 65, 0.065);

  border-radius:
    24px;

  background:
    #ffffff;

  box-shadow:
    0 10px 30px
    rgba(20, 30, 45, 0.045);
}

.empty-image-wrapper,
.state-icon {
  width: 76px;
  height: 76px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom:
    18px;

  border-radius:
    22px;

  background:
    rgba(40, 135, 247, 0.06);
}

.empty-image {
  width: 62px;
  height: 62px;

  object-fit:
    contain;
}

.state-icon svg {
  width: 30px;
  height: 30px;
}

.state-icon-error {
  color:
    #e58c45;

  background:
    rgba(229, 140, 69, 0.08);
}

.state-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;

  letter-spacing:
    -0.2px;

  color:
    #30353b;
}

.state-description {
  max-width: 340px;

  margin:
    10px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10.5px;
  line-height: 1.5;

  color:
    #8a9098;
}

/* ========================================================= */
/* EMPTY HINT */
/* ========================================================= */

.empty-hint {
  display: inline-flex;
  align-items: center;

  gap: 7px;

  margin-top:
    20px;

  padding:
    9px 12px;

  border:
    1px solid
    rgba(40, 135, 247, 0.08);

  border-radius:
    10px;

  background:
    rgba(40, 135, 247, 0.045);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  color:
    #75808c;
}

.empty-hint-heart {
  color:
    #2887f7;

  font-size: 12px;
}

/* ========================================================= */
/* RETRY */
/* ========================================================= */

.retry-button {
  min-height: 38px;

  margin-top:
    20px;

  padding:
    0 18px;

  border:
    1px solid
    rgba(40, 135, 247, 0.15);

  border-radius:
    12px;

  background:
    rgba(40, 135, 247, 0.075);

  color:
    #2887f7;

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  transition:
    background-color 0.18s ease,
    transform 0.18s ease;
}

.retry-button:hover {
  background:
    rgba(40, 135, 247, 0.12);

  transform:
    translateY(-1px);
}

/* ========================================================= */
/* TOAST */
/* ========================================================= */

.toast {
  position: fixed;

  z-index: 9999;

  left: 50%;
  bottom: 88px;

  display: inline-flex;
  align-items: center;

  gap: 8px;

  padding:
    10px 14px;

  border:
    1px solid
    rgba(255, 255, 255, 0.12);

  border-radius:
    12px;

  background:
    rgba(31, 36, 43, 0.94);

  color:
    #ffffff;

  transform:
    translateX(-50%);

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  box-shadow:
    0 12px 30px
    rgba(20, 30, 45, 0.18);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}

.toast-icon {
  width: 19px;
  height: 19px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius:
    50%;

  background:
    rgba(255, 255, 255, 0.12);
}

.toast-icon svg {
  width: 13px;
  height: 13px;
}

/* ========================================================= */
/* DARK */
/* ========================================================= */

html[data-theme="dark"] .favorites-container {
  background:
    linear-gradient(
      180deg,
      #191b1f 0%,
      #15171b 100%
    );

  color:
    #f0f1f3;
}

html[data-theme="dark"] .fixed-footer {
  background:
    #1c1e22;

  border-color:
    rgba(255, 255, 255, 0.055);
}

html[data-theme="dark"] .favorites-hero {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    0 12px 32px
    rgba(0, 0, 0, 0.16);
}

html[data-theme="dark"] .hero-title {
  color:
    #f2f3f4;
}

html[data-theme="dark"] .hero-label {
  color:
    #777e87;
}

html[data-theme="dark"] .hero-subtitle {
  color:
    #7f858d;
}

html[data-theme="dark"] .hero-label-icon {
  background:
    rgba(40, 135, 247, 0.1);
}

html[data-theme="dark"] .favorites-counter {
  background:
    rgba(40, 135, 247, 0.08);

  border-color:
    rgba(70, 150, 255, 0.09);
}

html[data-theme="dark"] .counter-label {
  color:
    #727983;
}

html[data-theme="dark"] .hero-glow {
  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.14) 0%,
      rgba(40, 135, 247, 0.035) 48%,
      transparent 72%
    );
}

html[data-theme="dark"] .hero-heart {
  color:
    rgba(80, 155, 255, 0.055);
}

html[data-theme="dark"] .state-card {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0 12px 32px
    rgba(0, 0, 0, 0.16);
}

html[data-theme="dark"] .state-title {
  color:
    #e8e9eb;
}

html[data-theme="dark"] .state-description {
  color:
    #777e87;
}

html[data-theme="dark"] .empty-image-wrapper {
  background:
    rgba(40, 135, 247, 0.075);
}

html[data-theme="dark"] .empty-hint {
  background:
    rgba(40, 135, 247, 0.055);

  border-color:
    rgba(70, 150, 255, 0.08);

  color:
    #777f89;
}

html[data-theme="dark"] .retry-button {
  background:
    rgba(40, 135, 247, 0.09);

  border-color:
    rgba(70, 150, 255, 0.11);
}

html[data-theme="dark"] .toast {
  background:
    rgba(38, 40, 46, 0.96);

  border-color:
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 34px
    rgba(0, 0, 0, 0.3);
}

/* ========================================================= */
/* ANIMATION */
/* ========================================================= */

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;

  transform:
    translateX(-50%)
    translateY(7px);
}

/* ========================================================= */
/* TABLET */
/* ========================================================= */

@media (max-width: 980px) {
  .favorites-page {
    max-width: 720px;

    padding:
      26px
      24px
      60px;
  }

  .favorites-grid {
    grid-template-columns:
      minmax(0, 1fr);

    row-gap: 22px;
  }

  .favorites-hero {
    min-height: 145px;
  }
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 600px) {
  .favorites-page {
    padding:
      16px
      14px
      42px;
  }

  .favorites-hero {
    min-height: 0;

    align-items:
      flex-start;

    padding:
      20px 18px;

    border-radius:
      18px;
  }

  .hero-title {
    font-size: 20px;
  }

  .hero-subtitle {
    max-width: 90%;

    font-size: 10.5px;
  }

  .favorites-counter {
    display: none;
  }

  .hero-decoration {
    width: 150px;
  }

  .hero-heart {
    width: 90px;
    height: 90px;

    right: 10px;
    bottom: -34px;
  }

  .favorites-grid {
    margin-top: 18px;

    row-gap: 18px;
  }

  .state-wrapper {
    min-height: 380px;

    padding:
      30px 0;
  }

  .state-card {
    padding:
      30px 20px;

    border-radius:
      20px;
  }

  .toast {
    width:
      calc(100% - 32px);

    max-width:
      360px;

    justify-content:
      center;

    box-sizing:
      border-box;

    text-align:
      center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .retry-button,
  .toast-fade-enter-active,
  .toast-fade-leave-active {
    transition: none;
  }
}
</style>